/* ═══════════════════════════════════════════════════════════════
   PiARIS — Lightbox modal for issue screenshots

   Full-viewport overlay that displays an image, supports prev/next
   navigation, keyboard (ArrowLeft / ArrowRight / Escape), and a close
   button. Used by IssueScreenshots but also reusable for any image
   gallery in the app.

   Props:
     images:   [{ url, caption?, uploadedBy?, uploadedAt?, source? }]
     index:    currently displayed image index
     onClose:  called when user closes
     onIndex:  called with new index when user navigates

   Accessibility:
     - role=dialog, aria-modal, aria-label
     - focus trapped on the close button initially
     - Escape closes; Tab cycles among action buttons
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P } from "../constants";

var E = React.createElement;

function Lightbox(props) {
  var images = props.images || [];
  var idx = typeof props.index === "number" ? props.index : 0;
  var count = images.length;

  React.useEffect(function () {
    if (count === 0) return;
    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); props.onClose && props.onClose(); }
      else if (e.key === "ArrowRight" && count > 1) {
        e.preventDefault(); props.onIndex && props.onIndex((idx + 1) % count);
      }
      else if (e.key === "ArrowLeft" && count > 1) {
        e.preventDefault(); props.onIndex && props.onIndex((idx - 1 + count) % count);
      }
    }
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, [idx, count, props.onClose, props.onIndex]);

  if (count === 0) return null;
  var img = images[idx] || {};

  return E("div", {
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Screenshot viewer",
    onClick: function (e) { if (e.target === e.currentTarget) props.onClose && props.onClose(); },
    style: {
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(0,0,0,0.88)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24
    }
  },
    /* Close button — top right */
    E("button", {
      "aria-label": "Close screenshot viewer",
      onClick: props.onClose,
      style: {
        position: "absolute", top: 14, right: 14,
        width: 40, height: 40, borderRadius: "50%",
        background: "rgba(255,255,255,0.12)", border: "none", color: "#fff",
        fontSize: 22, cursor: "pointer", lineHeight: 1
      }
    }, "\u00D7"),

    /* Prev arrow */
    count > 1 ? E("button", {
      "aria-label": "Previous screenshot",
      onClick: function () { props.onIndex && props.onIndex((idx - 1 + count) % count); },
      style: {
        position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
        width: 44, height: 44, borderRadius: "50%",
        background: "rgba(255,255,255,0.12)", border: "none", color: "#fff",
        fontSize: 24, cursor: "pointer"
      }
    }, "\u2039") : null,

    /* Next arrow */
    count > 1 ? E("button", {
      "aria-label": "Next screenshot",
      onClick: function () { props.onIndex && props.onIndex((idx + 1) % count); },
      style: {
        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
        width: 44, height: 44, borderRadius: "50%",
        background: "rgba(255,255,255,0.12)", border: "none", color: "#fff",
        fontSize: 24, cursor: "pointer"
      }
    }, "\u203A") : null,

    /* Image + caption */
    E("figure", { style: { margin: 0, maxWidth: "90vw", maxHeight: "90vh", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 } },
      E("img", {
        src: img.url,
        alt: img.caption || "Issue screenshot",
        style: { maxWidth: "100%", maxHeight: "78vh", objectFit: "contain", borderRadius: 6, boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }
      }),
      E("figcaption", {
        style: { color: "#fff", fontSize: 13, textAlign: "center", maxWidth: 720, lineHeight: 1.5 }
      },
        img.caption ? E("div", { style: { fontWeight: 600, marginBottom: 4 } }, img.caption) : null,
        E("div", { style: { fontSize: 11, color: "rgba(255,255,255,0.65)" } },
          (count > 1 ? ("Image " + (idx + 1) + " of " + count) : ""),
          img.source ? (count > 1 ? " \u2022 " : "") + (img.source === "extension" ? "Auto-captured by extension" : "Manual upload") : null,
          img.uploadedBy ? " \u2022 " + img.uploadedBy : null
        )
      )
    )
  );
}

export { Lightbox };
